import React, { useState, useEffect, useRef, useContext, useImperativeHandle, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  listClienteComments,
  createClienteComment,
  updateClienteComment,
  deleteClienteComment,
} from "../../services/kanban";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canShowMenu } from "../../helpers/menuAccess";
import AgendamentoDetalhes from "../AgendamentoDetalhes";
import useSettings from "../../hooks/useSettings";
import { normalizeMediaUrl } from "../../utils/mediaUrl";
import MediaGalleryViewer from "../MediaGalleryViewer";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxHeight: "1500px",
  },
  commentsContainer: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark"
      ? theme.palette.background.default
      : "#f5f5f5",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.type === "dark" ? "#2b2b2b" : "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.type === "dark" ? "#555" : "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.type === "dark" ? "#777" : "#555",
    },
  },
  commentItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark"
      ? theme.palette.background.paper
      : "#ffffff",
    borderRadius: "8px",
    boxShadow: theme.palette.type === "dark"
      ? "0 1px 2px rgba(0,0,0,0.4)"
      : "0 1px 2px rgba(0,0,0,0.1)",
    position: "relative",
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    justifyContent: "space-between",
  },
  commentUserInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
  },
  userName: {
    fontWeight: 600,
    fontSize: "14px",
  },
  commentDate: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
  },
  commentContent: {
    marginTop: theme.spacing(1),
    fontSize: "14px",
    color: theme.palette.text.primary,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    tabSize: 4,
  },
  tabularCommentContent: {
    marginTop: theme.spacing(1),
    margin: 0,
    fontSize: "13px",
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    tabSize: 4,
    overflowX: "auto",
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    lineHeight: 1.4,
  },
  commentMedia: {
    marginTop: theme.spacing(1),
    maxWidth: "100%",
  },
  commentImage: {
    maxWidth: "300px",
    maxHeight: "300px",
    borderRadius: "8px",
    cursor: "pointer",
  },
  commentFile: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "4px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  inputContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark"
      ? theme.palette.background.paper
      : "#ffffff",
    borderTop: `1px solid ${theme.palette.divider}`,
    overflowY: "unset",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.type === "dark" ? "#2b2b2b" : "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.type === "dark" ? "#555" : "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.type === "dark" ? "#777" : "#555",
    },
  },
  inputBox: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  textInput: {
    flex: 1,
  },
  attachmentPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "4px",
    marginBottom: theme.spacing(1),
  },
  previewImage: {
    maxWidth: "100px",
    maxHeight: "100px",
    borderRadius: "4px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.text.secondary,
  },
  // Fundo fixo claro deixava os campos ilegiveis no tema escuro (o MUI pinta os
  // textos de branco). action.hover e um realce sutil sobre o fundo do painel
  // nos dois temas — um cinza/preto fixo destoaria do container em volta.
  relacionamentoFields: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: "none",
    // Icone nativo de calendario/relogio e preto: sem inverter ele some no dark.
    "& input::-webkit-calendar-picker-indicator": {
      filter: theme.palette.type === "dark" ? "invert(1)" : "none",
    },
  },
  checkboxContainer: {
    marginBottom: theme.spacing(1),
  },
  // Azul escuro do "+ Novo tipo..." some no fundo escuro do menu.
  addNewOption: {
    color: theme.palette.type === "dark" ? "#90caf9" : "#1976d2",
    fontStyle: "italic",
  },
  observacoesBox: {
    padding: 12,
    backgroundColor: theme.palette.action.hover,
  },
}));

const EDIT_COMMENT_PERMISSION = "kanban_editar_comentarios";
const COMMENTS_PAGE_SIZE = 3;
const KANBAN_CONTACT_METHODS_SETTING_KEY = "kanbanEnabledRelationshipContactMethods";
const LEGACY_CONTACT_METHOD_LABELS = {
  VISITA: "Visita",
  ORCAMENTO: "Orçamento",
  FOLLOW_UP: "Follow-up",
  NEGOCIACAO: "Negociação",
  POS_VENDA: "Pós-venda",
  RECLAMACAO: "Reclamação",
  INDICACAO: "Indicação",
};
const DEFAULT_CONTACT_METHODS = ["Visita", "Orçamento", "Follow-up", "Negociação", "Pós-venda", "Reclamação", "Indicação", "Tráfego Pago", "Prospecção"];

const normalizeContactMethodLabel = (value) => String(value || "").replace(/\s+/g, " ").trim();

const dedupeContactMethods = (items = []) => {
  const unique = [];
  const seen = new Set();

  for (const item of items) {
    const normalized = normalizeContactMethodLabel(item);
    if (!normalized) continue;

    const key = normalized
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    if (seen.has(key)) continue;

    seen.add(key);
    unique.push(normalized);
  }

  return unique;
};

const parseEnabledContactMethods = (rawValue) => {
  if (!rawValue) {
    return DEFAULT_CONTACT_METHODS;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return DEFAULT_CONTACT_METHODS;
    }

    const normalized = dedupeContactMethods(
      parsed.map((value) => {
        const raw = normalizeContactMethodLabel(value);
        return LEGACY_CONTACT_METHOD_LABELS[String(raw).toUpperCase()] || raw;
      })
    );

    return normalized.length > 0 ? Array.from(new Set(normalized)) : DEFAULT_CONTACT_METHODS;
  } catch (_error) {
    return DEFAULT_CONTACT_METHODS;
  }
};

const getDefaultRelacionamentoData = () => ({
  data_contato: new Date().toISOString().split("T")[0],
  hora_contato: new Date().toTimeString().split(" ")[0].slice(0, 5),
  meio_contato: "",
  contatoId: "",
  resultado_visita: "",
  proxima_acao: "",
  data_proxima_acao: "",
  observacoes_internas: "",
  potencial_vendas: "",
  acoes_vendas: "",
});

const getDefaultFollowupData = () => ({
  assunto: "",
  descricao: "",
  data_followup: new Date().toISOString().split("T")[0],
  hora_followup: new Date().toTimeString().split(" ")[0].slice(0, 5),
  status: "pendente",
});

// Modo card: sem cliente vinculado, o componente registra/lista relacionamentos do
// card/atendimento (endpoints /kanban/cards/:cardId/historico); comentário comum e
// anexos ficam indisponíveis porque dependem da timeline do cliente.
const hasImageAttachment = (comment) => comment?.commentType === "image" && Boolean(comment.mediaUrl);

const toCommentViewerItem = (comment) => ({
  id: String(comment.id),
  type: "image",
  url: normalizeMediaUrl(comment.mediaUrl),
  fileName: comment.mediaName || "imagem",
  createdAt: comment.createdAt,
  message: comment,
});

const ClienteComments = React.forwardRef(({ clienteId, cardId, contactId, contactName }, ref) => {
  const classes = useStyles();
  const isCardMode = !clienteId && Boolean(cardId);
  const { user, menus } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  // Visualizador de imagens: navega pelas imagens dos comentários já carregados.
  const [viewerItem, setViewerItem] = useState(null);
  const viewerItems = useMemo(
    () => (viewerItem ? comments.filter(hasImageAttachment).map(toCommentViewerItem) : undefined),
    [viewerItem, comments]
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  // Alteracao por GitHub Copilot em 2026-03-23: exigir confirmacao antes de excluir comentario.
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  const fileInputRef = useRef(null);
  const commentsContainerRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);
  const shouldPreserveScrollRef = useRef(false);
  const previousScrollTopRef = useRef(0);
  const previousScrollHeightRef = useRef(0);
  
  // Estados para relacionamento
  const [isRelacionamento, setIsRelacionamento] = useState(true);
  const [registrarFollowup, setRegistrarFollowup] = useState(false);
  const [enabledContactMethods, setEnabledContactMethods] = useState(DEFAULT_CONTACT_METHODS);
  const [contatos, setContatos] = useState([]);
  const [relacionamentoData, setRelacionamentoData] = useState(getDefaultRelacionamentoData());
  const [followupData, setFollowupData] = useState(getDefaultFollowupData());
  const [newContactMethodDialogOpen, setNewContactMethodDialogOpen] = useState(false);
  const [newContactMethodInput, setNewContactMethodInput] = useState("");
  const [savingNewContactMethod, setSavingNewContactMethod] = useState(false);
  const { update: updateSetting } = useSettings();
  
  // Estados para modal de visualização
  const [relacionamentoModal, setRelacionamentoModal] = useState(false);
  const [relacionamentoDetalhes, setRelacionamentoDetalhes] = useState(null);
  const [loadingRelacionamento, setLoadingRelacionamento] = useState(false);
  const [followupModal, setFollowupModal] = useState(false);
  const [followupDetalhes, setFollowupDetalhes] = useState(null);
  const [loadingFollowup, setLoadingFollowup] = useState(false);

  const scrollToBottom = (behavior = "smooth") => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTo({
        top: commentsContainerRef.current.scrollHeight,
        behavior,
      });
    }
  };

  useEffect(() => {
    if (clienteId) {
      loadComments({ reset: true });
      loadContatos();
      loadKanbanRelationshipSettings();
    } else if (cardId) {
      loadCardHistoricos({ reset: true });
      loadKanbanRelationshipSettings();
      setContatos(contactId ? [{ id: contactId, nome: contactName || "Contato do atendimento" }] : []);
      setIsRelacionamento(true);
      setRelacionamentoData((prev) => ({ ...prev, contatoId: contactId || "" }));
    } else {
      setComments([]);
      setHasMore(false);
      setPageNumber(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, cardId]);

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;

    if (shouldPreserveScrollRef.current) {
      const heightDiff = container.scrollHeight - previousScrollHeightRef.current;
      container.scrollTop = previousScrollTopRef.current + heightDiff;
      shouldPreserveScrollRef.current = false;
      return;
    }

    if (shouldScrollToBottomRef.current) {
      scrollToBottom("auto");
      shouldScrollToBottomRef.current = false;
    }
  }, [comments]);

  const loadComments = async ({ reset = false } = {}) => {
    const nextPage = reset ? 1 : pageNumber + 1;

    try {
      if (reset) {
        setLoading(true);
      } else {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
      }

      const response = await listClienteComments(clienteId, {
        pageNumber: nextPage,
        limit: COMMENTS_PAGE_SIZE,
      });

      const payload = response.data || {};
      const newComments = Array.isArray(payload) ? payload : (payload.comments || []);
      const nextHasMore = Array.isArray(payload) ? false : Boolean(payload.hasMore);

      setHasMore(nextHasMore);
      setPageNumber(nextPage);

      if (reset) {
        shouldScrollToBottomRef.current = true;
        setComments(newComments);
      } else {
        setComments((prevComments) => [...newComments, ...prevComments]);
      }
    } catch (error) {
      console.error("Erro ao carregar comentários:", error);
      toast.error("Erro ao carregar comentários");
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  // Modo card: cada histórico vira um objeto comment-like para reaproveitar o render
  // e os links "Ver Relacionamento"/"Ver Followup" (readOnly esconde editar/excluir).
  const mapHistoricoToComment = (historico) => {
    const followup = Array.isArray(historico.followups) && historico.followups.length > 0
      ? historico.followups[0]
      : null;

    const content = [
      `📋 Relacionamento registrado: ${historico.meio_contato || "Não informado"}`,
      followup ? `📌 Follow-up: ${followup.assunto}` : null,
      historico.observacoes || null,
    ]
      .filter(Boolean)
      .join("\n\n");

    return {
      id: `hist-${historico.id}`,
      commentType: "text",
      content,
      createdAt: historico.createdAt || historico.data_contato,
      user: historico.user,
      userId: historico.userId,
      readOnly: true,
      metadata: {
        tipo: "relacionamento",
        meio_contato: historico.meio_contato || null,
        historicoId: historico.id,
        followupId: null,
      },
    };
  };

  const loadCardHistoricos = async ({ reset = false } = {}) => {
    const nextPage = reset ? 1 : pageNumber + 1;

    try {
      if (reset) {
        setLoading(true);
      } else {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
      }

      const { data } = await api.get(`/kanban/cards/${cardId}/historico`, {
        params: { page: nextPage, limit: COMMENTS_PAGE_SIZE },
      });

      // Backend devolve do mais recente para o mais antigo; a lista renderiza do
      // mais antigo (topo) para o mais recente (base), como os comentários.
      const mapped = (data?.historicos || []).map(mapHistoricoToComment).reverse();

      setHasMore(Boolean(data?.hasMore));
      setPageNumber(nextPage);

      if (reset) {
        shouldScrollToBottomRef.current = true;
        setComments(mapped);
      } else {
        setComments((prevComments) => [...mapped, ...prevComments]);
      }
    } catch (error) {
      console.error("Erro ao carregar relacionamentos do atendimento:", error);
      toast.error("Erro ao carregar relacionamentos do atendimento");
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const handleCommentsScroll = (event) => {
    if (event.currentTarget.scrollTop > 40) return;
    if (!hasMore || loading || loadingMore) return;

    previousScrollTopRef.current = event.currentTarget.scrollTop;
    previousScrollHeightRef.current = event.currentTarget.scrollHeight;
    shouldPreserveScrollRef.current = true;

    if (isCardMode) {
      loadCardHistoricos({ reset: false });
    } else {
      loadComments({ reset: false });
    }
  };

  const loadContatos = async () => {
    try {
      const { data } = await api.get(`/clientes/${clienteId}/contatos`);
      const contatosData = Array.isArray(data) ? data : (data.contatos || []);
      setContatos(contatosData);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const loadKanbanRelationshipSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      const setting = Array.isArray(data)
        ? data.find((item) => item.key === KANBAN_CONTACT_METHODS_SETTING_KEY)
        : null;
      const methods = parseEnabledContactMethods(setting?.value);
      setEnabledContactMethods(methods);

      // Autor: GitHub Copilot | Data: 24/04/2026 | Motivo: limpar meio de contato quando a opção deixa de existir na configuração do Kanban.
      if (relacionamentoData.meio_contato && !methods.includes(relacionamentoData.meio_contato)) {
        setRelacionamentoData((prev) => ({ ...prev, meio_contato: "" }));
      }
    } catch (error) {
      console.error("Erro ao carregar configuração de meios de contato:", error);
      setEnabledContactMethods(DEFAULT_CONTACT_METHODS);
    }
  };

  // Autor: Claude | Data: 22/07/2026 | Motivo: upload reaproveitado pelos dois fluxos (comentário simples e relacionamento).
  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.post("/clientes/comments/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return {
      commentType: file.type.startsWith("image/") ? "image" : "file",
      mediaUrl: data.url,
      mediaName: data.originalname,
      mediaMimeType: data.mimetype,
      mediaSize: data.size,
    };
  };

  const handleSendComment = async () => {
    if (!isRelacionamento && !newComment.trim() && !attachment) {
      return false;
    }

    try {
      setSending(true);

      // Se for relacionamento, enviar como histórico de relacionamento
      if (isRelacionamento) {
        const isFollowupRelacionamento = registrarFollowup;

        // Validar campo obrigatório
        if (!relacionamentoData.meio_contato) {
          toast.error("Meio de Contato é obrigatório");
          setSending(false);
          return false;
        }

        if (isFollowupRelacionamento && (!followupData.assunto || !followupData.data_followup || !followupData.hora_followup)) {
          toast.error("Para registrar follow-up, preencha assunto, data e hora");
          setSending(false);
          return false;
        }

        const parsedClienteId = Number.parseInt(clienteId, 10);
        if (!isCardMode && (!Number.isInteger(parsedClienteId) || parsedClienteId <= 0)) {
          toast.error("Cliente inválido para registro de relacionamento");
          setSending(false);
          return false;
        }

        const dataHoraContato = `${relacionamentoData.data_contato}T${relacionamentoData.hora_contato}:00`;
        const dataContatoDate = new Date(dataHoraContato);
        if (Number.isNaN(dataContatoDate.getTime())) {
          toast.error("Data e hora do contato inválidas");
          setSending(false);
          return false;
        }
        
        const dataProximaAcao = relacionamentoData.data_proxima_acao 
          ? `${relacionamentoData.data_proxima_acao}T12:00:00`
          : null;

        const payload = {
          data_contato: dataHoraContato,
          meio_contato: relacionamentoData.meio_contato,
          observacoes: newComment,
          status: "concluido",
          motivos: [relacionamentoData.meio_contato]
        };

        if (!isCardMode) {
          payload.clienteId = parsedClienteId;
        }

        // Vincula o histórico ao card mesmo quando há cliente (rastreabilidade)
        if (cardId) {
          payload.cardId = cardId;
        }
        
        if (relacionamentoData.resultado_visita) {
          payload.resultado_visita = relacionamentoData.resultado_visita;
        }

        if (relacionamentoData.proxima_acao) {
          payload.proxima_acao = relacionamentoData.proxima_acao;
        }

        if (dataProximaAcao) {
          payload.data_proxima_acao = dataProximaAcao;
        }

        if (relacionamentoData.observacoes_internas) {
          payload.observacoes_internas = relacionamentoData.observacoes_internas;
        }

        if (relacionamentoData.acoes_vendas) {
          payload.acoes_vendas = relacionamentoData.acoes_vendas;
        }

        if (relacionamentoData.potencial_vendas) {
          const parsedPotencial = parseFloat(
            relacionamentoData.potencial_vendas.toString().replace(/[^\d,.-]/g, "").replace(",", ".")
          );

          if (!Number.isNaN(parsedPotencial)) {
            payload.potencial_vendas = parsedPotencial;
          }
        }

        // Adicionar contatoId se selecionado
        if (relacionamentoData.contatoId && relacionamentoData.contatoId !== "") {
          payload.contatoId = parseInt(relacionamentoData.contatoId);
        }

        if (isFollowupRelacionamento) {
          const dataFollowup = `${followupData.data_followup}T${followupData.hora_followup}:00`;
          const followupDate = new Date(dataFollowup);
          if (Number.isNaN(followupDate.getTime())) {
            toast.error("Data e hora do follow-up inválidas");
            setSending(false);
            return false;
          }

          payload.followup = {
            data_followup: dataFollowup,
            assunto: followupData.assunto,
            descricao: newComment || undefined,
            status: followupData.status || "pendente"
          };

          if (relacionamentoData.contatoId && relacionamentoData.contatoId !== "") {
            payload.followup.contatoId = parseInt(relacionamentoData.contatoId);
          }
        }

        // Autor: Claude | Data: 22/07/2026 | Motivo: anexo era descartado silenciosamente ao registrar
        // relacionamento; sobe o arquivo antes e envia junto do histórico.
        // Anexo depende do comentário automático na timeline do cliente (não existe em modo card).
        if (attachment && !isCardMode) {
          const uploaded = await uploadAttachment(attachment);
          payload.anexo = {
            mediaUrl: uploaded.mediaUrl,
            mediaName: uploaded.mediaName,
            mediaMimeType: uploaded.mediaMimeType,
            mediaSize: uploaded.mediaSize,
          };
        }

        console.log("Salvando relacionamento:", payload);
        if (isCardMode) {
          await api.post(`/kanban/cards/${cardId}/historico`, payload);
          await loadCardHistoricos({ reset: true });
        } else {
          await api.post(`/clientes/${clienteId}/historico`, payload);
          // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: comentário é criado automaticamente no backend ao registrar relacionamento.
          await loadComments({ reset: true });
        }

        toast.success(
          isFollowupRelacionamento
            ? "Relacionamento e follow-up registrados com sucesso!"
            : "Relacionamento registrado com sucesso!"
        );

        // Resetar formulário (em modo card o envio é sempre relacionamento)
        setNewComment("");
        setIsRelacionamento(isCardMode);
        setRegistrarFollowup(false);
        setRelacionamentoData(getDefaultRelacionamentoData());
        setFollowupData(getDefaultFollowupData());
        if (isCardMode && contactId) {
          setRelacionamentoData((prev) => ({ ...prev, contatoId: contactId }));
        }
      } else {
        // Lógica original para comentários
        let commentData = {
          commentType: "text",
          content: newComment,
        };

        // Se houver anexo, fazer upload primeiro
        if (attachment) {
          const uploaded = await uploadAttachment(attachment);

          commentData = {
            ...uploaded,
            content: newComment.trim() ? newComment : null,
          };
        }

        const response = await createClienteComment(clienteId, commentData);
        shouldScrollToBottomRef.current = true;
        setComments((prevComments) => [...prevComments, response.data]);
        setNewComment("");
        toast.success("Comentário enviado com sucesso!");
      }
      
      setAttachment(null);
      setAttachmentPreview(null);
      return true;
    } catch (error) {
      console.error("Erro ao enviar:", error);
      toastError(error);
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleSaveNewContactMethod = async () => {
    const normalized = String(newContactMethodInput || "").replace(/\s+/g, " ").trim();
    if (!normalized) return;

    const key = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const alreadyExists = enabledContactMethods.some(
      (m) => m.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === key
    );
    if (alreadyExists) {
      toast.warning("Esse meio de contato já existe.");
      setRelacionamentoData((prev) => ({ ...prev, meio_contato: normalized }));
      setNewContactMethodDialogOpen(false);
      return;
    }

    setSavingNewContactMethod(true);
    try {
      const nextMethods = [...enabledContactMethods, normalized];
      await updateSetting({ key: KANBAN_CONTACT_METHODS_SETTING_KEY, value: JSON.stringify(nextMethods) });
      setEnabledContactMethods(nextMethods);
      setRelacionamentoData((prev) => ({ ...prev, meio_contato: normalized }));
      setNewContactMethodDialogOpen(false);
      toast.success(`"${normalized}" adicionado como meio de contato.`);
    } catch (error) {
      toast.error("Erro ao salvar novo meio de contato.");
    } finally {
      setSavingNewContactMethod(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      hasPendingComment: () => Boolean(newComment.trim() || attachment),
      // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: permitir que o botão Salvar do card
      // também persista o comentário digitado quando houver rascunho pendente.
      savePendingComment: async () => {
        if (!newComment.trim() && !attachment) {
          return { attempted: false, saved: true };
        }

        const saved = await handleSendComment();
        return { attempted: true, saved };
      }
    }),
    [newComment, attachment, handleSendComment]
  );

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setAttachment(file);

    // Criar preview para imagens
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachmentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const canEditComment = (comment) => {
    if (!comment || !user) return false;
    // Históricos renderizados em modo card são somente leitura
    if (comment.readOnly) return false;

    const currentUserId = Number(user.id);
    const commentOwnerId = Number(comment.userId || comment.user?.id);
    const isAuthor = currentUserId === commentOwnerId;
    const hasPermission = canShowMenu(user, menus, EDIT_COMMENT_PERMISSION);

    return isAuthor || hasPermission;
  };

  const handleStartEdit = () => {
    if (!selectedComment || !canEditComment(selectedComment)) return;

    setEditCommentId(selectedComment.id);
    setEditCommentContent(selectedComment.content || "");
    handleCloseMenu();
  };

  const handleCancelEdit = () => {
    setEditCommentId(null);
    setEditCommentContent("");
  };

  const handleSaveEdit = async () => {
    if (!editCommentId) return;

    const editingComment = comments.find((comment) => comment.id === editCommentId);
    if (!editingComment || !canEditComment(editingComment)) {
      toast.error("Você não tem permissão para editar este comentário");
      return;
    }

    if (!editCommentContent.trim() && !editingComment.mediaUrl) {
      toast.error("O comentário requer conteúdo ou mídia");
      return;
    }

    try {
      const { data } = await updateClienteComment(editCommentId, {
        content: editCommentContent,
      });

      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment.id === editCommentId ? data : comment
        )
      );

      toast.success("Comentário atualizado com sucesso!");
      handleCancelEdit();
    } catch (error) {
      console.error("Erro ao editar comentário:", error);
      toast.error("Erro ao editar comentário");
    }
  };

  const handleOpenMenu = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  const handleRequestDeleteComment = () => {
    if (!selectedComment) return;

    setCommentToDelete(selectedComment);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteClienteComment(commentToDelete.id);
      setComments((prevComments) => prevComments.filter((c) => c.id !== commentToDelete.id));
      toast.success("Comentário excluído com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir comentário:", error);
      toast.error("Erro ao excluir comentário");
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendComment();
    }
  };

  const formatDate = (date) => {
    try {
      return format(parseISO(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return date;
    }
  };

  const handleViewRelacionamento = async (comment) => {
    try {
      console.log("📋 Ver relacionamento clicado:", comment);
      console.log("📋 Metadata raw:", comment.metadata);
      
      const metadata = parseCommentMetadata(comment);
      
      console.log("📋 Metadata parsed:", metadata);
      
      if (metadata.historicoId) {
        setLoadingRelacionamento(true);
        setRelacionamentoModal(true);
        
        try {
          const historicoUrl = isCardMode
            ? `/kanban/cards/${cardId}/historico/${metadata.historicoId}`
            : `/clientes/${clienteId}/historico/${metadata.historicoId}`;
          const { data } = await api.get(historicoUrl);
          console.log("📋 Relacionamento carregado:", data);
          setRelacionamentoDetalhes(data);
        } catch (error) {
          console.error("Erro ao carregar relacionamento:", error);
          toast.error("Erro ao carregar detalhes do relacionamento");
          setRelacionamentoModal(false);
        } finally {
          setLoadingRelacionamento(false);
        }
      } else {
        console.warn("📋 historicoId não encontrado no metadata");
        toast.info("Histórico de relacionamento não encontrado");
      }
    } catch (error) {
      console.error("Erro ao abrir relacionamento:", error);
      toast.error("Erro ao abrir relacionamento");
    }
  };

  const handleViewFollowup = async (comment) => {
    try {
      const metadata = parseCommentMetadata(comment);
      const followupId = metadata.followupId;
      const historicoId = metadata.historicoId;

      setLoadingFollowup(true);
      setFollowupModal(true);

      try {
        if (followupId) {
          const { data } = await api.get(`/agendamentos/${followupId}`);
          setFollowupDetalhes(data);
        } else if (historicoId) {
          // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: manter acesso ao follow-up pelo comentário mesmo quando metadata.followupId não vier preenchido.
          const historicoUrl = isCardMode
            ? `/kanban/cards/${cardId}/historico/${historicoId}`
            : `/clientes/${clienteId}/historico/${historicoId}`;
          const { data } = await api.get(historicoUrl);
          const followup = data?.followups?.[0];

          if (!followup) {
            toast.info("Follow-up não encontrado para este comentário");
            setFollowupModal(false);
            return;
          }

          setFollowupDetalhes({
            id: followup.id || null,
            titulo: followup.assunto || "Follow-up",
            descricao: followup.descricao || followup.assunto || "",
            dataInicio: followup.data_followup,
            dataFim: followup.data_followup,
            tipo: "followup",
            status: followup.status || "pendente",
            prioridade: "media",
            clienteId: data?.clienteId || clienteId,
            contatoId: followup.contatoId || data?.contatoId || null,
            cliente: data?.cliente || null,
            contato: followup.contato || data?.contato || null,
            user: data?.user || null,
            createdAt: followup.createdAt || data?.createdAt,
            observacoes: followup.descricao || null,
            readOnly: true,
          });
        } else {
          toast.info("Follow-up não encontrado para este comentário");
          setFollowupModal(false);
        }
      } catch (error) {
        console.error("Erro ao carregar follow-up:", error);
        toast.error("Erro ao carregar detalhes do follow-up");
        setFollowupModal(false);
      } finally {
        setLoadingFollowup(false);
      }
    } catch (error) {
      console.error("Erro ao abrir follow-up:", error);
      toast.error("Erro ao abrir follow-up");
    }
  };

  const parseCommentMetadata = (comment) => {
    if (!comment?.metadata) return {};
    if (typeof comment.metadata === "object") return comment.metadata;

    try {
      return JSON.parse(comment.metadata);
    } catch (error) {
      return {};
    }
  };

  const isRelacionamentoComment = (comment) => {
    try {
      if (comment.content && comment.content.includes('📋 Relacionamento registrado:')) {
        return true;
      }
      const metadata = parseCommentMetadata(comment);
      // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: tratar como relacionamento quando houver historicoId para habilitar links no comentário automático.
      return metadata.tipo === 'relacionamento' || Boolean(metadata.historicoId);
    } catch (error) {
      return false;
    }
  };

  const hasFollowupInComment = (comment) => {
    const metadata = parseCommentMetadata(comment);
    const hasFollowupByMetadata = Boolean(metadata.followupId);
    const hasFollowupByHistorico =
      Boolean(metadata.historicoId) && String(metadata.meio_contato || "").toUpperCase() === "FOLLOW_UP";
    const hasFollowupByContent = Boolean(comment?.content?.includes("📌 Follow-up:"));

    return hasFollowupByMetadata || hasFollowupByHistorico || hasFollowupByContent;
  };

  const renderCommentContent = (comment) => {
    const isRelacionamento = isRelacionamentoComment(comment);
    const hasFollowup = hasFollowupInComment(comment);
    const isEditing = editCommentId === comment.id;
    const hasTabularText = typeof comment?.content === "string" && /\t/.test(comment.content);

    if (isEditing) {
      return (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            variant="outlined"
            size="small"
            value={editCommentContent}
            onChange={(e) => setEditCommentContent(e.target.value)}
            placeholder="Editar comentário..."
          />
          <Box style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Button size="small" color="primary" variant="contained" onClick={handleSaveEdit}>
              Salvar
            </Button>
            <Button size="small" variant="outlined" onClick={handleCancelEdit}>
              Cancelar
            </Button>
          </Box>
        </Box>
      );
    }
    
    // Autor: Claude | Data: 22/07/2026 | Motivo: comentário de relacionamento pode ter anexo; render
    // único garante que a mídia e os botões "Ver Relacionamento"/"Ver Followup" apareçam juntos.
    const normalizedMediaUrl = comment.mediaUrl ? normalizeMediaUrl(comment.mediaUrl) : null;
    const isImageComment = comment.commentType === "image" && normalizedMediaUrl;
    const isFileComment = comment.commentType === "file" && normalizedMediaUrl;

    return (
      <Box>
        {comment.content && (
          hasTabularText ? (
            <Typography component="pre" className={classes.tabularCommentContent} translate="no">
              {comment.content}
            </Typography>
          ) : (
            <Typography className={classes.commentContent} translate="no">
              {comment.content}
            </Typography>
          )
        )}
        {isImageComment && (
          <Box className={classes.commentMedia}>
            <img
              src={normalizedMediaUrl}
              alt={comment.mediaName}
              className={classes.commentImage}
              onClick={() => setViewerItem(toCommentViewerItem(comment))}
              onError={(e) => {
                console.warn("Failed to load comment image:", normalizedMediaUrl);
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}
        {isFileComment && (
          <Box
            className={classes.commentFile}
            style={{ marginTop: 8 }}
            onClick={() => window.open(normalizedMediaUrl, "_blank")}
          >
            <AttachFileIcon fontSize="small" />
            <Typography variant="body2">{comment.mediaName || "Anexo"}</Typography>
          </Box>
        )}
        {isRelacionamento && (
          <Box style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Tooltip title="Ver detalhes do relacionamento">
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => handleViewRelacionamento(comment)}
                style={{ padding: 4 }}
              >
                <AssignmentIcon fontSize="small" />
                <Typography variant="caption" style={{ marginLeft: 4 }}>
                  Ver Relacionamento
                </Typography>
              </IconButton>
            </Tooltip>
            {hasFollowup && (
              <Tooltip title="Ver detalhes do follow-up">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleViewFollowup(comment)}
                  style={{ padding: 4 }}
                >
                  <VisibilityIcon fontSize="small" />
                  <Typography variant="caption" style={{ marginLeft: 4 }}>
                    Ver Followup
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (!clienteId && !cardId) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body2">
          Selecione um card com cliente para ver os comentários
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box
        className={classes.commentsContainer}
        ref={commentsContainerRef}
        onScroll={handleCommentsScroll}
      >
        {loadingMore && (
          <Box style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {loading ? (
          <Box className={classes.emptyState}>
            <CircularProgress />
          </Box>
        ) : comments.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              {isCardMode
                ? "Nenhum relacionamento registrado para este atendimento."
                : "Nenhum comentário ainda. Seja o primeiro a comentar!"}
            </Typography>
          </Box>
        ) : (
          <>
            {comments.map((comment) => (
              <Paper key={comment.id} className={classes.commentItem}>
                <Box className={classes.commentHeader}>
                  <Box className={classes.commentUserInfo}>
                    <Avatar className={classes.avatar}>
                      {comment.user?.name?.charAt(0).toUpperCase() || "U"}
                    </Avatar>
                    <Box>
                      <Typography className={classes.userName}>
                        {comment.user?.name || "Usuário"}
                      </Typography>
                      <Typography className={classes.commentDate}>
                        {formatDate(comment.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  {!comment.readOnly && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, comment)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {renderCommentContent(comment)}
              </Paper>
            ))}
          </>
        )}
      </Box>

      <Box className={classes.inputContainer}>
        {/* Checkbox de Relacionamento (em modo card o envio é sempre relacionamento) */}
        <Box className={classes.checkboxContainer}>
          {!isCardMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRelacionamento}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setIsRelacionamento(nextChecked);

                    if (!nextChecked) {
                      setRegistrarFollowup(false);
                      setFollowupData(getDefaultFollowupData());
                    }
                  }}
                  color="primary"
                />
              }
              label="Registrar como relacionamento"
            />
          )}

          {isRelacionamento && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={registrarFollowup}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setRegistrarFollowup(nextChecked);

                    if (!nextChecked) {
                      setFollowupData(getDefaultFollowupData());
                    }
                  }}
                  color="primary"
                />
              }
              label="Registrar follow-up"
            />
          )}
        </Box>

        {/* Campos de Relacionamento */}
        {isRelacionamento && (
          <Box className={classes.relacionamentoFields}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12 }}>
              Dados do Relacionamento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Data"
                  type="date"
                  size="small"
                  value={relacionamentoData.data_contato}
                  onChange={(e) => setRelacionamentoData({ ...relacionamentoData, data_contato: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Hora"
                  type="time"
                  size="small"
                  value={relacionamentoData.hora_contato}
                  onChange={(e) => setRelacionamentoData({ ...relacionamentoData, hora_contato: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Meio de Contato *</InputLabel>
                  <Select
                    value={relacionamentoData.meio_contato}
                    onChange={(e) => {
                      if (e.target.value === "__add_new__") {
                        setNewContactMethodInput("");
                        setNewContactMethodDialogOpen(true);
                      } else {
                        setRelacionamentoData({ ...relacionamentoData, meio_contato: e.target.value });
                      }
                    }}
                    label="Meio de Contato *"
                  >
                    {enabledContactMethods.length === 0 && (
                      <MenuItem value="__no_contact_methods" disabled>
                        Nenhum meio de contato habilitado em Configurações &gt; Kanban
                      </MenuItem>
                    )}
                    {enabledContactMethods.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value="__add_new__" className={classes.addNewOption}>
                      + Novo tipo...
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Diálogo para adicionar novo meio de contato */}
              <Dialog open={newContactMethodDialogOpen} onClose={() => setNewContactMethodDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Novo Meio de Contato</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Nome do tipo"
                    variant="outlined"
                    value={newContactMethodInput}
                    onChange={(e) => setNewContactMethodInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newContactMethodInput.trim()) {
                        e.preventDefault();
                        handleSaveNewContactMethod();
                      }
                    }}
                    style={{ marginTop: 8 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setNewContactMethodDialogOpen(false)} disabled={savingNewContactMethod}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveNewContactMethod}
                    color="primary"
                    variant="contained"
                    disabled={!newContactMethodInput.trim() || savingNewContactMethod}
                  >
                    {savingNewContactMethod ? <CircularProgress size={20} /> : "Adicionar"}
                  </Button>
                </DialogActions>
              </Dialog>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" disabled={contatos.length === 0}>
                  <InputLabel>Contato</InputLabel>
                  <Select
                    value={relacionamentoData.contatoId}
                    onChange={(e) => setRelacionamentoData({ ...relacionamentoData, contatoId: e.target.value })}
                    label="Contato"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {contatos.length === 0 && (
                      <MenuItem value="__no_contacts" disabled>
                        Nenhum contato cadastrado
                      </MenuItem>
                    )}
                    {contatos.map((contato) => (
                      <MenuItem key={contato.id} value={contato.id}>
                        {contato.nome || contato.NOME}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {registrarFollowup && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                      Dados do Follow-up
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Assunto do Follow-up *"
                      size="small"
                      value={followupData.assunto}
                      onChange={(e) => setFollowupData({ ...followupData, assunto: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Data *"
                      type="date"
                      size="small"
                      value={followupData.data_followup}
                      onChange={(e) => setFollowupData({ ...followupData, data_followup: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Hora *"
                      type="time"
                      size="small"
                      value={followupData.hora_followup}
                      onChange={(e) => setFollowupData({ ...followupData, hora_followup: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        {attachment && (
          <Box className={classes.attachmentPreview}>
            {attachmentPreview ? (
              <img
                src={attachmentPreview}
                alt="Preview"
                className={classes.previewImage}
                onError={(e) => {
                  console.warn("Failed to load attachment preview");
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <AttachFileIcon />
            )}
            <Typography variant="body2" style={{ flex: 1 }}>
              {attachment.name}
            </Typography>
            <IconButton size="small" onClick={handleRemoveAttachment}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box className={classes.inputBox}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileSelect}
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
          />
          {!isCardMode && (
            <>
              <Tooltip title="Anexar imagem">
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={sending}
                >
                  <ImageIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Anexar arquivo">
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={sending}
                >
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <TextField
            className={classes.textInput}
            placeholder={isCardMode ? "Observações do relacionamento..." : "Comentar..."}
            multiline
            maxRows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            variant="outlined"
            size="small"
          />
          <IconButton
            color="primary"
            onClick={handleSendComment}
            disabled={sending || (!isRelacionamento && !newComment.trim() && !attachment)}
          >
            {sending ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {canEditComment(selectedComment) && (
          <MenuItem onClick={handleStartEdit}>
            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
            Editar
          </MenuItem>
        )}
        <MenuItem onClick={handleRequestDeleteComment}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Excluir
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir comentário</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este comentário?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="default">
            Cancelar
          </Button>
          <Button onClick={handleDeleteComment} color="secondary" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização do Relacionamento */}
      <Dialog
        open={relacionamentoModal}
        onClose={() => setRelacionamentoModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">Detalhes do Relacionamento</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingRelacionamento ? (
            <Box display="flex" justifyContent="center" padding={3}>
              <CircularProgress />
            </Box>
          ) : relacionamentoDetalhes ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data e Hora
                </Typography>
                <Typography variant="body1">
                  {relacionamentoDetalhes.data_contato 
                    ? format(parseISO(relacionamentoDetalhes.data_contato), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                    : "Não informado"}
                </Typography>
              </Grid>
              
              {/* Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: remover exibicao de status no modal de historico de relacionamento solicitado pelo usuario. */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Meio de Contato
                </Typography>
                <Chip 
                  label={relacionamentoDetalhes.meio_contato || "Não informado"} 
                  color="primary" 
                  size="small" 
                />
              </Grid>

              {relacionamentoDetalhes.contato && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contato
                  </Typography>
                  <Typography variant="body1">
                    {relacionamentoDetalhes.contato.nome || relacionamentoDetalhes.contato.NOME}
                  </Typography>
                  {(() => {
                    const c = relacionamentoDetalhes.contato;
                    let numero = "";
                    if (c.whatsapp) {
                      numero = c.whatsapp;
                    } else if (c.celular) {
                      numero = c.ddd_celular ? `(${c.ddd_celular}) ${c.celular}` : c.celular;
                    } else if (c.telefone) {
                      numero = c.ddd_telefone ? `(${c.ddd_telefone}) ${c.telefone}` : c.telefone;
                    }
                    return numero ? (
                      <Typography variant="body2" color="textSecondary">
                        {numero}
                      </Typography>
                    ) : null;
                  })()}
                </Grid>
              )}
              
              {relacionamentoDetalhes.observacoes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observações
                  </Typography>
                  <Paper className={classes.observacoesBox}>
                    <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                      {relacionamentoDetalhes.observacoes}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {relacionamentoDetalhes.user && (
                <Grid item xs={12}>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography variant="caption" color="textSecondary">
                    Registrado por {relacionamentoDetalhes.user.name} em{" "}
                    {format(parseISO(relacionamentoDetalhes.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography>Nenhum dado disponível</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRelacionamentoModal(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <AgendamentoDetalhes
        open={followupModal}
        onClose={() => {
          setFollowupModal(false);
          setFollowupDetalhes(null);
        }}
        agendamento={followupDetalhes}
        readOnly={Boolean(followupDetalhes?.readOnly)}
        onEdit={() => {}}
        onDelete={() => {
          setFollowupModal(false);
          setFollowupDetalhes(null);
        }}
        onConclude={(updatedAgendamento) => {
          setFollowupDetalhes(updatedAgendamento);
        }}
      />

      {loadingFollowup && (
        <Dialog open={loadingFollowup} maxWidth="xs" fullWidth>
          <DialogContent>
            <Box display="flex" justifyContent="center" padding={2}>
              <CircularProgress />
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {viewerItem && (
        <MediaGalleryViewer
          open
          onClose={() => setViewerItem(null)}
          initialItem={viewerItem}
          localItems={viewerItems}
        />
      )}
    </Box>
  );
});

export default ClienteComments;
